import { useEvent } from "@/context/event-context";
import { useGalleryImages } from "@/hooks/use-gallery-images";
import { Skeleton } from "@/components/ui/skeleton";

const Gallery = () => {
  const { event } = useEvent();
  const { data: images, isLoading } = useGalleryImages(event?.id, "general");

  // Need at least 6 images per row for the marquee to look right
  const photos = (images || []).map((img) => img.url);
  const half = Math.max(Math.ceil(photos.length / 2), 1);
  const row1 = photos.slice(0, half);
  const row2 = photos.slice(half);

  if (isLoading) {
    return (
      <section className="py-12 sm:py-[8vw] bg-white">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-[4vw]">
          <Skeleton className="h-8 w-48 mb-8" />
          <Skeleton className="h-[250px] w-full mb-4" />
          <Skeleton className="h-[250px] w-full" />
        </div>
      </section>
    );
  }

  if (photos.length === 0) return null;

  // Calculate animation distance based on actual image count per row
  const count1 = row1.length;
  const count2 = row2.length || 1;

  return (
    <section id="gallery" data-nav-theme="light" className="py-12 sm:py-[8vw] bg-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-[4vw]">
        <div className="text-center sm:text-left mb-8">
          <p
            className="font-heading uppercase text-muted-foreground mb-2"
            style={{
              fontSize: "var(--label-size, 0.85rem)",
              fontWeight: "var(--label-weight, 800)",
              letterSpacing: "var(--label-tracking, 0.2em)",
            }}
          >
            Previous Years
          </p>
          <h2
            className="font-display leading-[1.1]"
            style={{
              fontSize: "var(--section-size, clamp(2rem, 4vw, 3.5rem))",
              fontWeight: "var(--section-weight, 800)",
              letterSpacing: "var(--section-tracking, 0.1em)",
              fontStyle: "var(--section-style, normal)" as any,
              textTransform: "var(--hero-transform, uppercase)" as any,
            }}
          >
            The Legacy
          </h2>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4 overflow-hidden">
        {/* Row 1 — scroll left */}
        {row1.length > 0 && (
          <div className="relative">
            <div className="flex gap-3 sm:gap-4 gallery-scroll-left">
              {[...row1, ...row1, ...row1].map((src, i) => (
                <div key={i} className="shrink-0 w-[280px] sm:w-[350px] lg:w-[420px] aspect-[4/3] overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Row 2 — scroll right */}
        {row2.length > 0 && (
          <div className="relative">
            <div className="flex gap-3 sm:gap-4 gallery-scroll-right">
              {[...row2, ...row2, ...row2].map((src, i) => (
                <div key={i} className="shrink-0 w-[280px] sm:w-[350px] lg:w-[420px] aspect-[4/3] overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-6 sm:mt-8">
          <a
            href="/gallery"
            className="inline-block bg-gold text-ink px-6 py-3 font-sans font-semibold text-xs uppercase tracking-[0.15em] rounded-sm hover:-translate-y-0.5 transition-transform active:scale-[0.97]"
          >
            View All Photos
          </a>
        </div>

        <style>{`
          @keyframes gscroll-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(calc(-1 * (280px + 0.75rem) * ${count1})); }
          }
          @keyframes gscroll-right {
            0% { transform: translateX(calc(-1 * (280px + 0.75rem) * ${count2})); }
            100% { transform: translateX(0); }
          }
          .gallery-scroll-left { animation: gscroll-left ${count1 * 7}s linear infinite; }
          .gallery-scroll-right { animation: gscroll-right ${count2 * 7}s linear infinite; }
          @media (min-width: 640px) {
            @keyframes gscroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-1 * (350px + 1rem) * ${count1})); }
            }
            @keyframes gscroll-right {
              0% { transform: translateX(calc(-1 * (350px + 1rem) * ${count2})); }
              100% { transform: translateX(0); }
            }
          }
          @media (min-width: 1024px) {
            @keyframes gscroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-1 * (420px + 1rem) * ${count1})); }
            }
            @keyframes gscroll-right {
              0% { transform: translateX(calc(-1 * (420px + 1rem) * ${count2})); }
              100% { transform: translateX(0); }
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default Gallery;
